/**
 * Campaign (Bid Cycle) — Pure logic for graph generation and state transitions.
 * No DOM, no Three.js.
 */
import { SeededRandom } from './random';
import { createDefaultInventory } from './campaignStore';
import { createDefaultRoster, recruitUnit as rosterRecruitUnit, reviveAllUnits, resetAllUnitAp, spendUnitAp } from './campaignRoster';
import type {
  CampaignState,
  CampaignNode,
  CampaignBattleConfig,
  CampaignNodeStatus,
  CampaignSaveData,
  GameMode,
  StarType,
  MapLayout,
  PlanetSizePreset,
  SystemPreviewBody,
  WeaponKey,
  CampaignUnit,
  PlanetState,
  HazardState,
  FloraDestructionState,
} from '../types';
import { HAZARD_HEALTH } from '../constants';
import { dcos, dsin } from './dmath';

/** Max AP across all alive roster units — drives campaign action availability. */
export function getGlobalAp(roster: CampaignUnit[]): number {
  return roster.reduce((max, u) => u.alive ? Math.max(max, u.ap) : max, 0);
}

// =============================================================================
// CONSTANTS
// =============================================================================

const NUM_LAYERS = 11;
const STARTING_AP = 1;
const STARTING_MONEY = 20;

const NODE_NAMES = [
  'Outpost Kappa', 'Depot Sigma', 'Relay Theta', 'Station Omicron', 'Hub Epsilon',
  'Post Lambda', 'Waypoint Delta', 'Bastion Zeta', 'Cache Rho', 'Node Phi',
  'Marker Tau', 'Point Alpha', 'Array Gamma', 'Beacon Iota', 'Nexus Pi',
  'Terminal Nu', 'Anchor Xi', 'Pylon Mu', 'Ledger Chi', 'Vault Psi',
  'Spire Eta', 'Core Omega', 'Silo Beta', 'Watch Upsilon', 'Gate Digamma',
];

// Planet palette colors (subset of PLANET_PALETTES from constants.ts — no import needed)
const PREVIEW_COLORS = [
  '#5a8a6a', '#6a7a9a', '#9a6a5a', '#7a6a8a', '#8a8a6a',
  '#6a8a8a', '#8a7060', '#607060', '#8a8a98',
];
const STAR_COLOR = '#ffe4a0';

/**
 * Generate a lightweight 2D preview of the system layout.
 * Uses its own RNG — doesn't need to match actual mapgen exactly,
 * just be representative (correct planet count, similar sizes, nice spread).
 */
export function generateSystemPreview(config: Pick<CampaignBattleConfig, 'seed' | 'numStars' | 'numPlanets' | 'planetSize'>): SystemPreviewBody[] {
  const rng = new SeededRandom(`${config.seed}-preview`);
  const bodies: SystemPreviewBody[] = [];

  // Coordinate space: centered at 0,0 — will be normalized to fit canvas later
  const hasStars = config.numStars > 0;

  // Stars at center
  for (let i = 0; i < config.numStars; i++) {
    const starRadius = rng.range(1.8, 2.5);
    const angle = config.numStars > 1 ? (i / config.numStars) * Math.PI * 2 : 0;
    const dist = config.numStars > 1 ? 2 : 0;
    bodies.push({
      x: dcos(angle) * dist,
      y: dsin(angle) * dist,
      radius: starRadius,
      isStar: true,
      color: STAR_COLOR,
    });
  }

  // Planets in orbital rings around center (or scattered if no stars)
  for (let i = 0; i < config.numPlanets; i++) {
    const sizeScale = config.planetSize === 'small' ? 0.6
      : config.planetSize === 'large' ? 1.2 : 0.85;
    const radius = rng.range(0.5, 1.2) * sizeScale;
    const color = PREVIEW_COLORS[i % PREVIEW_COLORS.length];

    if (hasStars) {
      // Orbital placement
      const orbitDist = 5 + i * rng.range(1.8, 2.8);
      const angle = rng.range(0, Math.PI * 2);
      bodies.push({ x: dcos(angle) * orbitDist, y: dsin(angle) * orbitDist, radius, isStar: false, color });
    } else {
      // Scattered placement
      const angle = rng.range(0, Math.PI * 2);
      const dist = rng.range(2, 6 + config.numPlanets * 0.8);
      bodies.push({ x: dcos(angle) * dist, y: dsin(angle) * dist, radius, isStar: false, color });
    }
  }

  return bodies;
}

// =============================================================================
// GRAPH GENERATION
// =============================================================================

/**
 * Pick unique subgrid cells for nodes in a layer.
 * Grid is 3 cols × 5 rows. Centered layers use (1, 2).
 * Cells are spread vertically first to avoid clustering.
 */
function pickSubgridCells(
  count: number,
  centered: boolean,
  rng: SeededRandom
): Array<{ col: number; row: number }> {
  if (centered) {
    return [{ col: 1, row: 2 }]; // center of 3×5
  }

  // Build all 15 possible cells
  const allCells: Array<{ col: number; row: number }> = [];
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 3; c++) {
      allCells.push({ col: c, row: r });
    }
  }

  // Shuffle and pick, but ensure vertical spread:
  // First distribute across rows, then assign columns
  const rows = [0, 1, 2, 3, 4];
  // Shuffle rows
  for (let i = rows.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    [rows[i], rows[j]] = [rows[j], rows[i]];
  }

  const result: Array<{ col: number; row: number }> = [];
  for (let i = 0; i < count; i++) {
    const row = rows[i % rows.length];
    const col = rng.int(0, 2);
    result.push({ col, row });
  }

  // Sort by row so edge proximity logic works well
  result.sort((a, b) => a.row - b.row);
  return result;
}

export function generateCampaignGraph(campaignSeed: string): CampaignState {
  const rng = new SeededRandom(campaignSeed);
  const nodes: CampaignNode[] = [];
  let nodeId = 0;

  // Track nodes per layer for edge generation
  const layerNodes: number[][] = [];

  for (let layer = 0; layer < NUM_LAYERS; layer++) {
    // Fixed layer structure: single nodes at 0,1,4,7,10; fixed counts at 2,3; random elsewhere
    const LAYER_COUNTS: Record<number, number | null> = {
      0: 1, 1: 1, 2: 2, 3: 3, 4: 1, 7: 1, 10: 1,
    };
    const fixedCount = LAYER_COUNTS[layer];
    const count = fixedCount != null ? fixedCount : rng.int(2, 5);
    const centered = count === 1;
    const ids: number[] = [];

    // Pick unique subgrid cells (3 cols × 5 rows = 15 slots)
    const cells = pickSubgridCells(count, centered, rng);

    for (let i = 0; i < count; i++) {
      const { col, row } = cells[i];
      // Derive x (0-1 vertical) from subRow for edge proximity logic
      const x = (row + 0.5) / 5;

      const nameIndex = nodeId % NODE_NAMES.length;
      const name = NODE_NAMES[nameIndex];

      const battleConfig = generateBattleConfig(layer, nodeId, rng, campaignSeed);

      nodes.push({
        id: nodeId,
        layer,
        x,
        subCol: col,
        subRow: row,
        edges: [],
        battleConfig,
        status: layer === 0 ? 'available' : 'locked',
        name,
        systemPreview: generateSystemPreview(battleConfig),
      });

      ids.push(nodeId);
      nodeId++;
    }

    layerNodes.push(ids);
  }

  // Generate edges: connect each non-final-layer node to 1-3 next-layer nodes
  for (let layer = 0; layer < NUM_LAYERS - 1; layer++) {
    const currentIds = layerNodes[layer];
    const nextIds = layerNodes[layer + 1];

    // Track which next-layer nodes have incoming edges
    const hasIncoming = new Set<number>();

    // Each current node connects to 1-3 next-layer nodes (prefer spatially close)
    for (const srcId of currentIds) {
      const srcNode = nodes[srcId];
      const edgeCount = rng.int(1, Math.min(3, nextIds.length));

      // Sort next-layer nodes by distance to this node
      const ranked = [...nextIds].sort((a, b) => {
        return Math.abs(nodes[a].x - srcNode.x) - Math.abs(nodes[b].x - srcNode.x);
      });

      // Pick the closest ones (with some randomness)
      const selected = new Set<number>();
      for (let i = 0; i < edgeCount && i < ranked.length; i++) {
        // Sometimes skip to the next one for variety
        const pick = (i < ranked.length - 1 && rng.next() < 0.2) ? ranked[i + 1] : ranked[i];
        selected.add(pick);
        hasIncoming.add(pick);
      }

      srcNode.edges = [...selected];
    }

    // Ensure every next-layer node has at least one incoming edge
    for (const nextId of nextIds) {
      if (!hasIncoming.has(nextId)) {
        // Connect the spatially closest current-layer node
        let closest = currentIds[0];
        let closestDist = Infinity;
        for (const srcId of currentIds) {
          const dist = Math.abs(nodes[srcId].x - nodes[nextId].x);
          if (dist < closestDist) {
            closestDist = dist;
            closest = srcId;
          }
        }
        if (!nodes[closest].edges.includes(nextId)) {
          nodes[closest].edges.push(nextId);
        }
      }
    }
  }

  // Generate lateral edges: connect adjacent same-layer nodes (sorted by x)
  for (let layer = 0; layer < NUM_LAYERS; layer++) {
    const ids = layerNodes[layer];
    if (ids.length < 2) continue;

    // Sort by x position
    const sorted = [...ids].sort((a, b) => nodes[a].x - nodes[b].x);
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      // Bidirectional: add each to the other's edges
      if (!nodes[a].edges.includes(b)) nodes[a].edges.push(b);
      if (!nodes[b].edges.includes(a)) nodes[b].edges.push(a);
    }
  }

  // Assign recruitment nodes: spread across the campaign
  const RECRUIT_LAYERS = [1, 3, 5, 7, 9];
  for (const rl of RECRUIT_LAYERS) {
    const candidates = layerNodes[rl];
    if (candidates && candidates.length > 0) {
      const pick = candidates[rng.int(0, candidates.length - 1)];
      nodes[pick].battleConfig.recruitUnit = true;
    }
  }

  return {
    campaignSeed,
    nodes,
    currentLayer: 0,
    waveNumber: 0,
    maxLayer: NUM_LAYERS - 1,
    actionPoints: STARTING_AP,
    maxActionPoints: STARTING_AP,
    playerMoney: STARTING_MONEY,
    completedNodes: new Set(),
    lostNodes: new Set(),
    playerNodeId: 0,
    inventory: createDefaultInventory(),
    roster: createDefaultRoster(),
    permadeath: false,
    nodeHealthMultipliers: {},
  };
}

// =============================================================================
// DIFFICULTY SCALING
// =============================================================================

function scaleDifficulty(layer: number, rng: SeededRandom): {
  botDifficulty: string;
  botPersonality: string;
  numPlanets: number;
  numStars: number;
  numTeams: number;
  unitsPerTeam: number;
  botUnitsPerTeam: number;
  planetSize: PlanetSizePreset;
  reward: number;
  incomePerWave: number;
  botCbpBudget: number;
} {
  if (layer <= 2) {
    // Early: easy intro
    return {
      botDifficulty: 'easy',
      botPersonality: 'balanced',
      numPlanets: rng.int(4, 6),
      numStars: 1,
      numTeams: 2,
      unitsPerTeam: 1,
      botUnitsPerTeam: 1,
      planetSize: 'mixed',
      reward: rng.int(30, 50),
      incomePerWave: rng.int(10, 20),
      botCbpBudget: rng.int(8, 15),
    };
  } else if (layer <= 4) {
    // Early-mid: medium challenge
    return {
      botDifficulty: 'medium',
      botPersonality: rng.next() < 0.5 ? 'balanced' : 'aggressive',
      numPlanets: rng.int(6, 8),
      numStars: 1,
      numTeams: rng.int(2, 3),
      unitsPerTeam: 1,
      botUnitsPerTeam: rng.int(1, 2),
      planetSize: 'mixed',
      reward: rng.int(50, 80),
      incomePerWave: rng.int(15, 30),
      botCbpBudget: rng.int(12, 25),
    };
  } else if (layer <= 7) {
    // Mid-late: hard
    return {
      botDifficulty: rng.next() < 0.6 ? 'hard' : 'medium',
      botPersonality: rng.next() < 0.3 ? 'aggressive' : rng.next() < 0.5 ? 'chaotic' : 'balanced',
      numPlanets: rng.int(8, 10),
      numStars: rng.int(1, 2),
      numTeams: rng.int(2, 3),
      unitsPerTeam: rng.int(1, 2),
      botUnitsPerTeam: rng.int(2, 3),
      planetSize: rng.next() < 0.5 ? 'mixed' : 'large',
      reward: rng.int(80, 120),
      incomePerWave: rng.int(25, 40),
      botCbpBudget: rng.int(20, 35),
    };
  } else {
    // Late: hard/expert
    return {
      botDifficulty: rng.next() < 0.5 ? 'hard' : 'expert',
      botPersonality: rng.next() < 0.3 ? 'aggressive' : rng.next() < 0.5 ? 'chaotic' : 'balanced',
      numPlanets: rng.int(8, 12),
      numStars: rng.int(1, 2),
      numTeams: rng.int(3, 4),
      unitsPerTeam: rng.int(1, 2),
      botUnitsPerTeam: rng.int(2, 3),
      planetSize: rng.next() < 0.5 ? 'mixed' : 'large',
      reward: rng.int(100, 150),
      incomePerWave: rng.int(30, 50),
      botCbpBudget: rng.int(25, 45),
    };
  }
}

function pickGameMode(layer: number, rng: SeededRandom): GameMode {
  const earlyModes: GameMode[] = ['deathmatch', 'dominion'];
  const midModes: GameMode[] = ['deathmatch', 'dominion', 'sovereignty', 'ctf'];
  const lateModes: GameMode[] = ['deathmatch', 'dominion', 'sovereignty', 'ctf', 'uplink', 'signalTrace', 'salvageRun'];

  const pool = layer <= 2 ? earlyModes : layer <= 5 ? midModes : lateModes;
  return pool[rng.int(0, pool.length - 1)];
}

function pickMapLayout(rng: SeededRandom): MapLayout {
  const layouts: MapLayout[] = ['random', 'linear', 'ring', 'layered', 'spiral', 'clusters', 'scatter'];
  return layouts[rng.int(0, layouts.length - 1)];
}

function pickStarType(rng: SeededRandom): StarType | 'random' {
  const types: (StarType | 'random')[] = ['random', 'redDwarf', 'orangeDwarf', 'yellowDwarf', 'blueGiant', 'redGiant'];
  return types[rng.int(0, types.length - 1)];
}

function pickHazardDensity(layer: number, rng: SeededRandom): 'none' | 'some' | 'many' {
  if (layer <= 4) return rng.next() < 0.3 ? 'some' : 'none';
  return rng.next() < 0.4 ? 'many' : 'some';
}

function pickRuinDensity(rng: SeededRandom): 'none' | 'few' | 'some' | 'many' {
  const opts: Array<'none' | 'few' | 'some' | 'many'> = ['none', 'few', 'some', 'many'];
  return opts[rng.int(0, opts.length - 1)];
}

function generateBattleConfig(
  layer: number,
  nodeIndex: number,
  rng: SeededRandom,
  campaignSeed: string
): CampaignBattleConfig {
  const diff = scaleDifficulty(layer, rng);
  const gameMode = pickGameMode(layer, rng);

  return {
    seed: `${campaignSeed}-node-${nodeIndex}`,
    gameMode,
    numPlanets: diff.numPlanets,
    numStars: diff.numStars,
    starType: pickStarType(rng),
    mapLayout: pickMapLayout(rng),
    planetSize: diff.planetSize,
    botDifficulty: diff.botDifficulty,
    botPersonality: diff.botPersonality,
    numTeams: diff.numTeams,
    unitsPerTeam: diff.unitsPerTeam,
    botUnitsPerTeam: diff.botUnitsPerTeam,
    reward: diff.reward,
    incomePerWave: diff.incomePerWave,
    botCbpBudget: diff.botCbpBudget,
    hazardDensity: pickHazardDensity(layer, rng),
    ruinDensity: pickRuinDensity(rng),
  };
}

// =============================================================================
// SYSTEM HEALTH — scales income based on post-battle planetary condition
// =============================================================================

/**
 * Calculate system health multiplier (0–1) from post-battle state.
 * Averages three components equally:
 *   - Planet mass:  sum(mass) / sum(initialMass)
 *   - Flora:        alive count / initial count
 *   - Hazards:      sum(health) / sum(initial health)
 * Components with no initial value (e.g. no flora) count as 1.0.
 */
export function calculateSystemHealth(
  planets: PlanetState[],
  hazards: HazardState[],
  floraDestruction: FloraDestructionState | undefined,
): number {
  // Planet mass health
  let totalMass = 0;
  let totalInitialMass = 0;
  for (const p of planets) {
    if (!p.alive) continue;
    totalMass += p.mass;
    totalInitialMass += p.initialMass;
  }
  const planetHealth = totalInitialMass > 0 ? totalMass / totalInitialMass : 1.0;

  // Flora health
  let floraHealth = 1.0;
  if (floraDestruction && floraDestruction.initialCount > 0) {
    floraHealth = 1.0 - (floraDestruction.totalDestroyed / floraDestruction.initialCount);
  }

  // Hazard health
  let totalHazardHealth = 0;
  let totalHazardInitial = 0;
  for (const h of hazards) {
    totalHazardInitial += HAZARD_HEALTH[h.hazardType] ?? 0;
    totalHazardHealth += h.alive ? h.health : 0;
  }
  const hazardHealth = totalHazardInitial > 0 ? totalHazardHealth / totalHazardInitial : 1.0;

  // Average the three components (equal weight)
  return Math.max(0, Math.min(1, (planetHealth + floraHealth + hazardHealth) / 3));
}

// =============================================================================
// STATE TRANSITIONS
// =============================================================================

export function completeBattle(state: CampaignState, nodeId: number, selectedUnitIds?: number[], systemHealth?: number): CampaignState {
  const node = state.nodes.find(n => n.id === nodeId);
  if (!node) return state;

  const newCompleted = new Set(state.completedNodes);
  newCompleted.add(nodeId);

  // Spend AP on participating units
  let roster = selectedUnitIds
    ? spendUnitAp(state.roster, selectedUnitIds)
    : spendUnitAp(state.roster, [0]); // fallback: just commander

  // Recruit if battle config says so and roster not full
  if (node.battleConfig.recruitUnit) {
    roster = rosterRecruitUnit(roster, `${state.campaignSeed}-recruit-${nodeId}`);
  }

  const globalAp = getGlobalAp(roster);

  // Store system health multiplier for this node's recurring income
  const healthMults = { ...(state.nodeHealthMultipliers ?? {}), [nodeId]: systemHealth ?? 1.0 };

  const newState: CampaignState = {
    ...state,
    actionPoints: globalAp,
    playerMoney: state.playerMoney + node.battleConfig.reward,
    completedNodes: newCompleted,
    playerNodeId: nodeId,
    roster,
    nodeHealthMultipliers: healthMults,
  };

  return updateNodeStatuses(newState);
}

export function loseBattle(state: CampaignState, nodeId: number, selectedUnitIds?: number[]): CampaignState {
  const newLost = new Set(state.lostNodes);
  newLost.add(nodeId);

  // Spend AP on participating units
  const roster = selectedUnitIds
    ? spendUnitAp(state.roster, selectedUnitIds)
    : spendUnitAp(state.roster, [0]);

  const globalAp = getGlobalAp(roster);

  const newState: CampaignState = {
    ...state,
    actionPoints: globalAp,
    lostNodes: newLost,
    roster,
  };

  return updateNodeStatuses(newState);
}

export function advanceWave(state: CampaignState): CampaignState {
  if (state.currentLayer >= state.maxLayer) return state;

  // Income from completed nodes, scaled by system health multiplier
  const healthMults = state.nodeHealthMultipliers ?? {};
  let income = 0;
  for (const nodeId of state.completedNodes) {
    const node = state.nodes.find(n => n.id === nodeId);
    if (node) {
      const mult = healthMults[nodeId] ?? 1.0;
      income += Math.round(node.battleConfig.incomePerWave * mult);
    }
  }

  // Reset AP on all units + revive if not permadeath
  const newRoster = state.permadeath
    ? resetAllUnitAp(state.roster)
    : reviveAllUnits(state.roster); // reviveAllUnits already resets AP

  const globalAp = getGlobalAp(newRoster);

  // Lockout advances every 2 wave advances
  const newWave = (state.waveNumber ?? 0) + 1;
  const newLayer = (newWave % 2 === 0) ? state.currentLayer + 1 : state.currentLayer;

  const newState: CampaignState = {
    ...state,
    currentLayer: newLayer,
    waveNumber: newWave,
    actionPoints: globalAp,
    playerMoney: state.playerMoney + income,
    roster: newRoster,
  };

  return updateNodeStatuses(newState);
}

export function updateNodeStatuses(state: CampaignState): CampaignState {
  const nodes = state.nodes.map(node => {
    if (state.completedNodes.has(node.id)) {
      return { ...node, status: 'completed' as CampaignNodeStatus };
    }

    // Layer 0 is always available
    if (node.layer === 0) {
      return { ...node, status: 'available' as CampaignNodeStatus };
    }

    // Available if reachable from a completed node and player has AP
    const reachable = isNodeReachable(state, node.id);
    if (reachable && state.actionPoints > 0) {
      return { ...node, status: 'available' as CampaignNodeStatus };
    }

    return { ...node, status: 'locked' as CampaignNodeStatus };
  });

  return { ...state, nodes };
}

function isNodeReachable(state: CampaignState, nodeId: number): boolean {
  const node = state.nodes.find(n => n.id === nodeId);
  if (!node) return false;

  if (node.layer === 0) return true;

  // Reachable if any completed previous-layer node has a forward edge to it
  for (const prev of state.nodes) {
    if (prev.layer === node.layer - 1 && state.completedNodes.has(prev.id)) {
      if (prev.edges.includes(nodeId)) return true;
    }
  }

  // Reachable via lateral edge from a completed same-layer node
  for (const peer of state.nodes) {
    if (peer.layer === node.layer && peer.id !== nodeId && state.completedNodes.has(peer.id)) {
      if (peer.edges.includes(nodeId)) return true;
    }
  }

  return false;
}

export interface CampaignResult {
  outcome: 'win' | 'loss' | 'ongoing';
  reason: string;
}

export function getCampaignResult(state: CampaignState): CampaignResult {
  // Win: all final-layer nodes completed
  const finalNodes = state.nodes.filter(n => n.layer === state.maxLayer);
  if (finalNodes.length > 0 && finalNodes.every(n => state.completedNodes.has(n.id))) {
    return { outcome: 'win', reason: 'All objectives in the final sector secured.' };
  }

  // Win: at least one final-layer node completed
  if (finalNodes.some(n => state.completedNodes.has(n.id))) {
    return { outcome: 'win', reason: 'Campaign objectives achieved.' };
  }

  return { outcome: 'ongoing', reason: '' };
}

export function getCampaignSummary(state: CampaignState): {
  totalNodes: number;
  completedCount: number;
  lostCount: number;
  moneyEarned: number;
  layersCleared: number;
} {
  return {
    totalNodes: state.nodes.length,
    completedCount: state.completedNodes.size,
    lostCount: state.lostNodes.size,
    moneyEarned: state.playerMoney,
    layersCleared: state.currentLayer,
  };
}

// =============================================================================
// SERIALIZATION
// =============================================================================

export function serializeCampaign(state: CampaignState): CampaignSaveData {
  const nodeStatuses: Record<number, CampaignNodeStatus> = {};
  for (const node of state.nodes) {
    nodeStatuses[node.id] = node.status;
  }

  return {
    campaignSeed: state.campaignSeed,
    currentLayer: state.currentLayer,
    waveNumber: state.waveNumber,
    actionPoints: state.actionPoints,
    playerMoney: state.playerMoney,
    completedNodes: [...state.completedNodes],
    lostNodes: [...state.lostNodes],
    nodeStatuses,
    playerNodeId: state.playerNodeId,
    ownedWeapons: [...state.inventory.ownedWeapons],
    ammoStock: state.inventory.ammoStock,
    items: state.inventory.items,
    roster: state.roster,
    permadeath: state.permadeath,
    nodeHealthMultipliers: state.nodeHealthMultipliers ?? {},
    gearWeaponTiers: state.inventory.gearUpgrades.weaponTiers,
    gearSharedTiers: state.inventory.gearUpgrades.sharedTiers,
    gearItemTiers: state.inventory.gearUpgrades.itemTiers,
  };
}

export function deserializeCampaign(save: CampaignSaveData): CampaignState {
  // Regenerate the graph from seed (deterministic)
  const state = generateCampaignGraph(save.campaignSeed);

  // Apply saved state
  state.currentLayer = save.currentLayer;
  state.waveNumber = save.waveNumber ?? save.currentLayer * 2; // backwards compat
  state.actionPoints = save.actionPoints;
  state.playerMoney = save.playerMoney;
  state.completedNodes = new Set(save.completedNodes);
  state.lostNodes = new Set(save.lostNodes);
  state.playerNodeId = save.playerNodeId ?? 0;

  // Restore inventory (backwards compat: old saves get default)
  state.inventory = {
    ownedWeapons: new Set<WeaponKey>(save.ownedWeapons ?? ['standard', 'probe']),
    ammoStock: save.ammoStock ?? {},
    items: save.items ?? {},
    gearUpgrades: {
      weaponTiers: save.gearWeaponTiers ?? {},
      sharedTiers: save.gearSharedTiers ?? {},
      itemTiers: save.gearItemTiers ?? {},
    },
  };

  // Restore roster (backwards compat: old saves get default + ensure ap fields)
  state.roster = (save.roster ?? createDefaultRoster()).map(u => ({
    ...u,
    ap: u.ap ?? 1,
    maxAp: u.maxAp ?? 1,
  }));
  state.permadeath = save.permadeath ?? false;
  state.nodeHealthMultipliers = save.nodeHealthMultipliers ?? {};

  // Apply saved node statuses
  for (const node of state.nodes) {
    const savedStatus = save.nodeStatuses[node.id];
    if (savedStatus) {
      node.status = savedStatus;
    }
  }

  return state;
}

// =============================================================================
// BRIDGE: CampaignBattleConfig → MenuOptions-like
// =============================================================================

export interface CampaignMenuBridge {
  numPlayers: number;
  numBots: number;
  numPlanets: number;
  numStars: number;
  numWormholePairs: number;
  seed: string;
  botDifficulty: string;
  botPersonality: string;
  numTeams: number;
  unitsPerTeam: number;
  botUnitsPerTeam: number;
  humanTeams: number;
  botTeams: number;
  hazardDensity: 'none' | 'some' | 'many';
  ruinDensity: 'none' | 'few' | 'some' | 'many';
  planetSize: PlanetSizePreset;
  mapLayout: MapLayout;
  starType: StarType | 'random';
  gameMode: GameMode;
  cbpBudget: number;
  botCbpBudget: number;
  allowedWeapons?: string[];
}

export function battleConfigToBridge(config: CampaignBattleConfig, playerMoney: number): CampaignMenuBridge {
  const humanTeams = 1;
  const botTeams = config.numTeams - 1;

  return {
    numPlayers: config.unitsPerTeam,
    numBots: botTeams * config.botUnitsPerTeam,
    numPlanets: config.numPlanets,
    numStars: config.numStars,
    numWormholePairs: 0,
    seed: config.seed,
    botDifficulty: config.botDifficulty,
    botPersonality: config.botPersonality,
    numTeams: config.numTeams,
    unitsPerTeam: config.unitsPerTeam,
    botUnitsPerTeam: config.botUnitsPerTeam,
    humanTeams,
    botTeams,
    hazardDensity: config.hazardDensity,
    ruinDensity: config.ruinDensity,
    planetSize: config.planetSize,
    mapLayout: config.mapLayout,
    starType: config.starType,
    gameMode: config.gameMode,
    cbpBudget: playerMoney,
    botCbpBudget: config.botCbpBudget,
    allowedWeapons: config.allowedWeapons,
  };
}
